import { useState, useEffect, useCallback, useRef } from "react";
import { showErrorToast } from "../../../utils/toastUtils";
import { fetchRepoItems, fetchGroupRepoItems } from "../services/repoOrganizerService";

export function useRepoItems({
  role,
  isPersonalSpaceView,
  statusFilter,
  dayFilter,
  appliedSortOrder,
  isGroupView = false,
  selectedGroupId = null,
}) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const fetchAbortControllerRef = useRef(null);

  const fetchItems = useCallback(async () => {
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }

    const controller = new AbortController();
    fetchAbortControllerRef.current = controller;
    setIsLoading(true);

    try {
      let nextItems;

      if (isGroupView) {
        // Group Repository: fetch enterprise groups as root folders,
        // and if a specific group is selected, also fetch its contents.
        nextItems = await fetchGroupRepoItems({
          role,
          selectedGroupId,
          signal: controller.signal,
        });
      } else {
        nextItems = await fetchRepoItems({
          role,
          isPersonalSpaceView,
          statusFilter,
          dayFilter,
          appliedSortOrder,
          signal: controller.signal,
        });
      }

      if (controller.signal.aborted) return;

      setItems(nextItems);
    } catch (err) {
      if (
        err.name === "AbortError" ||
        err.name === "CanceledError" ||
        err.code === "ERR_CANCELED" ||
        controller.signal.aborted
      ) {
        console.log("Fetch cancelled");
        return;
      }
      console.error("Fetch items error:", err);
      showErrorToast("Failed to load repository items");
    } finally {
      if (fetchAbortControllerRef.current === controller) {
        setIsLoading(false);
      }
    }
  }, [
    role,
    isPersonalSpaceView,
    statusFilter,
    dayFilter,
    appliedSortOrder,
    isGroupView,
    selectedGroupId,
  ]);

  useEffect(() => {
    fetchItems();
    return () => {
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
      }
    };
  }, [fetchItems]);

  return { items, setItems, isLoading, setIsLoading, fetchItems };
}
